import {
  updateRuntimeStore,
  useEditorRuntimeStore,
} from '../stores/useEditorRuntimeStore';
import {
  cloneEditorState,
  defaultEditorStore,
  type EditorState,
  useEditorStore,
} from '../stores/useEditorStore';

import { resetLayerCounter } from './element';

export type HistoryEntry<TExternal = undefined> = {
  editorState: EditorState;
  externalState: TExternal | undefined;
};

export type ExternalStateCallbacks<T> = {
  getState: () => T;
  setState: (state: T) => void;
};

const historyState: HistoryEntry<unknown>[] = [
  {
    editorState: cloneEditorState(defaultEditorStore),
    externalState: undefined,
  },
];

let externalStateCallbacks: ExternalStateCallbacks<unknown> | null = null;

let isPaused = false;

export const history = {
  canRedo: (index: number) => index < historyState.length - 1,
  canUndo: (index: number) => index > 0,
  registerExternalStateCallbacks: <T>(callbacks: ExternalStateCallbacks<T>) => {
    externalStateCallbacks = callbacks as ExternalStateCallbacks<unknown>;
  },
  unregisterExternalStateCallbacks: () => {
    externalStateCallbacks = null;
  },
  pause: () => {
    isPaused = true;
  },
  resume: () => {
    isPaused = false;
  },
  push: (state: EditorState) => {
    if (isPaused) return;

    const currentHistoryIndex =
      useEditorRuntimeStore.getState().currentHistoryIndex;

    let externalState: unknown = undefined;
    if (externalStateCallbacks) {
      try {
        externalState = structuredClone(externalStateCallbacks.getState());
      } catch (error) {
        console.warn('Failed to clone external state for history:', error);
      }
    }

    if (currentHistoryIndex >= 0 && currentHistoryIndex < historyState.length) {
      const currentEntry = historyState[currentHistoryIndex];
      if (
        areHistoryEntriesEqual(currentEntry, {
          editorState: state,
          externalState,
        })
      ) {
        return;
      }
    }

    if (currentHistoryIndex < historyState.length - 1) {
      historyState.splice(currentHistoryIndex + 1);
    }

    historyState.push({
      editorState: cloneEditorState(state),
      externalState,
    });

    updateRuntimeStore({ currentHistoryIndex: historyState.length - 1 });
  },
  redo: () => {
    const currentHistoryIndex =
      useEditorRuntimeStore.getState().currentHistoryIndex;

    if (currentHistoryIndex < historyState.length - 1) {
      const nextEntry = historyState[currentHistoryIndex + 1];

      useEditorStore.setState(cloneEditorState(nextEntry.editorState));

      resetLayerCounter();

      if (externalStateCallbacks && nextEntry.externalState !== undefined) {
        try {
          externalStateCallbacks.setState(
            structuredClone(nextEntry.externalState),
          );
        } catch (error) {
          console.warn('Failed to restore external state during redo:', error);
        }
      }

      updateRuntimeStore({ currentHistoryIndex: currentHistoryIndex + 1 });
    }
  },
  undo: () => {
    const currentHistoryIndex =
      useEditorRuntimeStore.getState().currentHistoryIndex;

    if (currentHistoryIndex > 0) {
      const previousEntry = historyState[currentHistoryIndex - 1];

      useEditorStore.setState(cloneEditorState(previousEntry.editorState));

      resetLayerCounter();

      if (externalStateCallbacks && previousEntry.externalState !== undefined) {
        try {
          externalStateCallbacks.setState(
            structuredClone(previousEntry.externalState),
          );
        } catch (error) {
          console.warn('Failed to restore external state during undo:', error);
        }
      }

      updateRuntimeStore({ currentHistoryIndex: currentHistoryIndex - 1 });
    }
  },
  clear: () => {
    historyState.splice(0);
    updateRuntimeStore({ currentHistoryIndex: 0 });
  },
};

const areHistoryEntriesEqual = (
  entryA: HistoryEntry<unknown>,
  entryB: HistoryEntry<unknown>,
): boolean => {
  const { editorState: stateA, externalState: extA } = entryA;
  const { editorState: stateB, externalState: extB } = entryB;

  if (
    stateA.selectedElementIds.length !== stateB.selectedElementIds.length ||
    stateA.hoveredElementId !== stateB.hoveredElementId ||
    stateA.selectedSubElementIds.length !==
      stateB.selectedSubElementIds.length ||
    !stateA.selectedSubElementIds.every(
      (id, i) => id === stateB.selectedSubElementIds[i],
    ) ||
    stateA.lastUpdateHash !== stateB.lastUpdateHash ||
    stateA.polygonPointsVisible !== stateB.polygonPointsVisible
  ) {
    return false;
  }

  if (
    !stateA.selectedElementIds.every(
      (id, index) => id === stateB.selectedElementIds[index],
    )
  ) {
    return false;
  }

  if (stateA.elements.size !== stateB.elements.size) {
    return false;
  }

  for (const [key, element1] of stateA.elements) {
    const element2 = stateB.elements.get(key);
    try {
      if (!element2 || JSON.stringify(element1) !== JSON.stringify(element2)) {
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty
    } catch (_) {}
  }

  if (extA === undefined && extB === undefined) {
    return true;
  }

  if (extA === undefined || extB === undefined) {
    return false;
  }

  try {
    return JSON.stringify(extA) === JSON.stringify(extB);
  } catch {
    return false;
  }
};
